import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Breadcrumbs,
  Paper,
  Stack
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
// import { useAuth } from '../../context/AuthContext';
import { useSnackbar } from 'notistack';
import api from '../../services/api';

// Import form steps
import GeneralInfoForm from './forms/GeneralInfoForm';
import ScheduleForm from './forms/ScheduleForm';
import WorkPlanForm from './forms/WorkPlanForm';
import BudgetForm from './forms/BudgetForm';
import TeamForm from './forms/TeamForm';
// Hapus atau comment line 32:
// import ForemanForm from './forms/ForemanForm';
import ReviewForm from './forms/ReviewForm';
import { t } from '../../utils/textUtils';

const steps = [
  t('INFORMASI UMUM'),
  t('WAKTU & JADWAL'),
  t('RENCANA & VOLUME'),
  t('BIAYA & PENDANAAN'),
  t('TIM PELAKSANA'),
  t('REVIEW')
];

const EditProject = () => {
  // const { user } = useAuth(); // Uncomment jika diperlukan nanti
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [projectData, setProjectData] = useState({
    name: '',
    noPO: '',
    projectCode: '',
    description: '',
    jenisProyek: '',
    sektor: '',
    location: '',
    latitude: '',
    longitude: '',
    status: 'Planning',
    startDate: null,
    estimatedEndDate: null,
    totalAnggaran: '',
    luasTotalBangunan: '',
    workPlan: [],
    budget: {
      materials: [],
      labor: [],
      equipment: [],
      other: []
    },
    team: {
      projectManager: '',
      siteManager: '',
      qualityControl: '',
      healthSafetyOfficer: '',
      engineers: [],
      supervisors: []
    },
    siteEngineerIds: [],
    foremen: []
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [confirmUpdateDialog, setConfirmUpdateDialog] = useState(false);
  // const [originalProject, setOriginalProject] = useState(null);
  const [projectLoaded, setProjectLoaded] = useState(false);
  const [projectManagers, setProjectManagers] = useState([]);
  const [siteManagers, setSiteManagers] = useState([]);
  // Add missing states
  const [siteEngineers, setSiteEngineers] = useState([]);
  const [clients, setClients] = useState([]);
  const [productionCoordinators, setProductionCoordinators] = useState([]);

  // Debug state for team data
  useEffect(() => {
    console.log('Current state of team data:', {
      projectManagers,
      siteManagers,
      siteEngineers,
      clients,
      productionCoordinators
    });
  }, [projectManagers, siteManagers, siteEngineers, clients, productionCoordinators]);

  // Fetch project data and user lists
  useEffect(() => {
    const fetchProjectData = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/projects/${projectId}`);
        const project = response.data;
        
        // Format dates if they exist
        if (project.startDate) {
          project.startDate = new Date(project.startDate);
        }
        if (project.estimatedEndDate) {
          project.estimatedEndDate = new Date(project.estimatedEndDate);
        }

        const siteEngineerIds = Array.isArray(project.siteEngineers)
          ? project.siteEngineers.map(siteEngineer => siteEngineer.id)
          : (project.siteEngineerId ? [project.siteEngineerId] : []);
        project.siteEngineerIds = siteEngineerIds;
        
        setProjectData(project);
        // setOriginalProject(project);
        setProjectLoaded(true);
        
        // Fetch project managers
        try {
          const pmResponse = await api.get('/users/by-role/project_manager');
          if (pmResponse.data && pmResponse.data.users) {
            setProjectManagers(pmResponse.data.users);
          }
        } catch (err) {
          console.error('Error fetching project managers:', err);
        }
        
        // Fetch site managers
        try {
          const smResponse = await api.get('/users/by-role/site_manager');
          if (smResponse.data && smResponse.data.users) {
            setSiteManagers(smResponse.data.users);
          }
        } catch (err) {
          console.error('Error fetching site managers:', err);
        }
        
        // Add missing API calls
        // Fetch site engineers
        try {
          const seResponse = await api.get('/users/by-role/site_engineer');
          if (seResponse.data && seResponse.data.users) {
            setSiteEngineers(seResponse.data.users);
          }
        } catch (err) {
          console.error('Error fetching site engineers:', err);
        }
        
        // Fetch clients
        try {
          const clientResponse = await api.get('/clients');
          if (clientResponse.data && clientResponse.data.clients) {
            setClients(clientResponse.data.clients);
          }
        } catch (err) {
          console.error('Error fetching clients:', err);
        }
        
        // Fetch production coordinators
        try {
          const pcResponse = await api.get('/production-coordinators');
          if (pcResponse.data && pcResponse.data.productionCoordinators) {
            setProductionCoordinators(pcResponse.data.productionCoordinators);
          }
        } catch (err) {
          console.error('Error fetching production coordinators:', err);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching project:', err);
        setError('Failed to load project data. Please try again.');
        setLoading(false);
      }
    };
    
    fetchProjectData();
  }, [projectId]);

  // Handle form data changes
  const handleFormChange = (formData) => {
    setProjectData(prevData => ({
      ...prevData,
      ...formData
    }));
  };

  // Handle step navigation
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Open confirmation dialog before update
  const handleOpenUpdateDialog = () => {
    setConfirmUpdateDialog(true);
  };

  // Close confirmation dialog
  const handleCloseUpdateDialog = () => {
    setConfirmUpdateDialog(false);
  };

  // Handle project update
  const handleUpdateProject = async () => {
    setConfirmUpdateDialog(false);
    setLoading(true);
    try {
      // Prepare data for backend
      const dataToSend = {
        name: projectData.name,
        description: projectData.description,
        clientId: projectData.clientId,
        projectClientId: projectData.projectClientId,
        managerId: projectData.managerId,
        siteManagerId: projectData.siteManagerId,
        siteEngineerIds: Array.isArray(projectData.siteEngineerIds) ? projectData.siteEngineerIds : [],
        status: projectData.status || 'planning',
        // Include additional project fields
        jenisProyek: projectData.jenisProyek,
        sektor: projectData.sektor,
        kodeProyek: projectData.kodeProyek,
        noPO: projectData.noPO,
        location: projectData.location,
        latitude: projectData.latitude,
        longitude: projectData.longitude,
        durasiProyek: projectData.durasiProyek,
        jumlahUnitModular: projectData.jumlahUnitModular,
        tipePrefabrikasi: projectData.tipePrefabrikasi,
        luasTotalLahan: projectData.luasTotalLahan,
        luasTotalBangunan: projectData.luasTotalBangunan,
        totalAnggaran: projectData.totalAnggaran,
        sumberDana: projectData.sumberDana,
        rencanaPembayaran: projectData.rencanaPembayaran,
        // pemilikProyek removed as requested
        productionCoordinatorId: projectData.productionCoordinatorId,
        lokasiPabrikProduksi: projectData.lokasiPabrikProduksi,
        metodeKirim: projectData.metodeKirim,
        strukturUtama: projectData.strukturUtama,
        jenisDindingAtap: projectData.jenisDindingAtap,
        sumberListrikAir: projectData.sumberListrikAir,
        tingkatTahanCuacaIklim: projectData.tingkatTahanCuacaIklim,
        catatanTambahan: projectData.catatanTambahan
      };
      
      // Handle dates carefully - ensure they are strings
      if (projectData.startDate) {
        dataToSend.startDate = projectData.startDate instanceof Date 
          ? projectData.startDate.toISOString().split('T')[0] // Format as YYYY-MM-DD
          : String(projectData.startDate);
      }
      
      if (projectData.estimatedEndDate) {
        dataToSend.estimatedEndDate = projectData.estimatedEndDate instanceof Date 
          ? projectData.estimatedEndDate.toISOString().split('T')[0] // Format as YYYY-MM-DD
          : String(projectData.estimatedEndDate);
      }
      
      // Add other fields that are expected by the backend
      if (projectData.budget !== undefined) {
        dataToSend.budget = projectData.budget;
      }
      
      if (projectData.progress !== undefined) {
        dataToSend.progress = projectData.progress;
      }
      
      // Filter out undefined values
      Object.keys(dataToSend).forEach(key => {
        if (dataToSend[key] === undefined) {
          delete dataToSend[key];
        }
      });
      
      console.log('Sending project data:', dataToSend);
      const response = await api.put(`/projects/${projectId}`, dataToSend);
      console.log('Update response:', response);
      enqueueSnackbar('Project updated successfully!', { variant: 'success' });
      navigate(`/projects/${projectId}/detail`);
    } catch (err) {
      console.error('Error updating project:', err);
      console.error('Error details:', err.response?.data || err.message);
      setError(`Failed to update project: ${err.response?.data?.message || err.message}`);
      setLoading(false);
    }
  };

  // Handle cancel dialog
  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleCancel = () => {
    navigate(`/projects/${projectId}/detail`);
  };

  // Render form based on active step
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return <GeneralInfoForm 
                 formData={projectData} 
                 onChange={handleFormChange} 
               />;
      case 1:
        return <ScheduleForm 
                 formData={projectData} 
                 onChange={handleFormChange} 
               />;
      case 2:
        return <WorkPlanForm 
                 formData={projectData} 
                 onChange={handleFormChange} 
               />;
      case 3:
        return <BudgetForm 
                 formData={projectData} 
                 onChange={handleFormChange} 
               />;
      case 4:
        return <TeamForm 
                 formData={projectData} 
                 onChange={handleFormChange}
                 dropdownData={{
                   projectManagers,
                   siteManagers,
                   siteEngineers,
                   clients,
                   productionCoordinators
                 }}
               />;
      // case 5: // ForemanForm - disembunyikan sementara
      //   return <ForemanForm 
      //            formData={projectData} 
      //            onChange={handleFormChange} 
      //          />;
      case 5: // Langsung ke Review (sebelumnya case 6)
        // Combine all users and entities
        const allEntities = [
          ...projectManagers, 
          ...siteManagers, 
          ...siteEngineers, 
          ...clients, 
          ...productionCoordinators
        ];

        return <ReviewForm 
                 formData={projectData}
                 users={allEntities}
                 dropdownData={{
                   projectManagers,
                   siteManagers,
                   siteEngineers,
                   clients,
                   productionCoordinators
                 }}
               />;
      default:
        return 'Unknown step';
    }
  };

  if (loading && !projectLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress sx={{ color: '#3B82F6' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header with blue gradient */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4 },
          mb: 4,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #0f172a, #1e3a5f, #1e40af)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '40%',
            height: '100%',
            background: 'radial-gradient(circle at top right, rgba(59, 130, 246, 0.2), transparent 70%)',
            pointerEvents: 'none'
          }
        }}
      >
        <Box position="relative" zIndex={1}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#ffffff', mb: 1 }}>
            EDIT PROYEK
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>
            {projectData.name}
          </Typography>
        </Box>
      </Paper>

      {/* Breadcrumbs */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'rgba(15, 23, 42, 0.1)',
          bgcolor: '#F8FAFC'
        }}
      >
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" sx={{ color: '#64748B' }} />} 
          aria-label="breadcrumb"
        >
          <Typography 
            sx={{ 
              cursor: 'pointer', 
              color: '#64748B', 
              fontWeight: 600,
              '&:hover': { color: '#3B82F6', textDecoration: 'underline' } 
            }}
            onClick={() => navigate('/projects')}
          >
            PROYEK
          </Typography>
          <Typography 
            sx={{ 
              cursor: 'pointer', 
              color: '#64748B', 
              fontWeight: 600,
              '&:hover': { color: '#3B82F6', textDecoration: 'underline' } 
            }}
            onClick={() => navigate(`/projects/${projectId}/detail`)}
          >
            {projectData.name}
          </Typography>
          <Typography sx={{ color: '#1e40af', fontWeight: 700 }}>EDIT</Typography>
        </Breadcrumbs>
      </Paper>
      
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            border: '1px solid rgba(239, 68, 68, 0.2)',
            bgcolor: 'rgba(254, 226, 226, 0.5)'
          }}
        >
          {error}
        </Alert>
      )}
      
      {/* Main Content */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'rgba(15, 23, 42, 0.1)',
          bgcolor: '#FFFFFF',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.06)',
          mb: 3
        }}
      >
        {/* Stepper */}
        <Paper
          elevation={0}
          sx={{
            mb: 4,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'rgba(15, 23, 42, 0.1)',
            bgcolor: '#F8FAFC',
            overflow: 'hidden'
          }}
        >
          <Stepper 
            activeStep={activeStep} 
            alternativeLabel 
            sx={{ 
              py: 2.5, 
              px: { xs: 1, sm: 2 },
              '& .MuiStepLabel-label': {
                fontSize: '0.85rem',
                fontWeight: 600,
                color: '#64748B',
                '&.Mui-active': {
                  color: '#1e40af',
                  fontWeight: 700
                },
                '&.Mui-completed': {
                  color: '#059669',
                  fontWeight: 600
                }
              },
              '& .MuiStepIcon-root': {
                color: '#CBD5E1',
                '&.Mui-active': {
                  color: '#3B82F6'
                },
                '&.Mui-completed': {
                  color: '#059669'
                }
              },
              '& .MuiStepConnector-line': {
                borderColor: '#E2E8F0'
              },
              '& .MuiStepConnector-active': {
                '& .MuiStepConnector-line': {
                  borderColor: '#3B82F6'
                }
              }
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>
      
        {/* Form Content */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'rgba(15, 23, 42, 0.1)',
            bgcolor: '#FFFFFF',
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
            mb: 4
          }}
        >
          {renderStepContent(activeStep)}
        </Paper>
      
        {/* Action Buttons */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'rgba(15, 23, 42, 0.1)',
            bgcolor: '#F8FAFC',
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)'
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={activeStep === 0 ? handleOpenDialog : handleBack}
              sx={{ 
                borderColor: 'rgba(15, 23, 42, 0.2)',
                color: '#0f172a',
                fontWeight: 600,
                '&:hover': { 
                  backgroundColor: 'rgba(59, 130, 246, 0.04)',
                  borderColor: 'rgba(59, 130, 246, 0.4)'
                }
              }}
            >
              {activeStep === 0 ? 'BATAL' : 'KEMBALI'}
            </Button>
            
            <Box>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleOpenUpdateDialog}
                  disabled={loading}
                  sx={{ 
                    bgcolor: 'linear-gradient(135deg, #3B82F6, #1e40af)',
                    '&:hover': { bgcolor: 'linear-gradient(135deg, #2563EB, #1e3a8a)' },
                    color: '#ffffff',
                    fontWeight: 700,
                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                    textTransform: 'none',
                    px: 3
                  }}
                >
                  {loading ? <CircularProgress size={24} sx={{ color: '#ffffff' }} /> : 'PERBARUI PROYEK'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  sx={{ 
                    bgcolor: 'linear-gradient(135deg, #3B82F6, #1e40af)',
                    '&:hover': { bgcolor: 'linear-gradient(135deg, #2563EB, #1e3a8a)' },
                    color: '#ffffff',
                    fontWeight: 700,
                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                    textTransform: 'none',
                    px: 3
                  }}
                >
                  LANJUT
                </Button>
              )}
            </Box>
          </Stack>
        </Paper>
      </Paper>
      
      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description"
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 10px 40px rgba(15, 23, 42, 0.15)'
          }
        }}
      >
        <DialogTitle id="cancel-dialog-title" sx={{ 
          backgroundColor: 'linear-gradient(135deg, #0f172a, #1e3a5f)',
          color: '#ffffff',
          fontWeight: 800,
          py: 2.5,
          px: 3
        }}>
          BATAL PENGEDITAN?
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <DialogContentText id="cancel-dialog-description" sx={{ color: '#475569', fontSize: '1rem' }}>
            YAKIN INGIN BATAL? SEMUA PERUBAHAN AKAN HILANG.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(15, 23, 42, 0.1)', gap: 1 }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{ 
              color: '#64748B',
              fontWeight: 600,
              '&:hover': { 
                backgroundColor: 'rgba(15, 23, 42, 0.04)'
              }
            }}
          >
            TIDAK, LANJUTKAN EDIT
          </Button>
          <Button 
            onClick={handleCancel} 
            autoFocus
            variant="contained"
            color="error"
            sx={{
              fontWeight: 700,
              px: 3
            }}
          >
            YA, BATAL
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Update Confirmation Dialog */}
      <Dialog
        open={confirmUpdateDialog}
        onClose={handleCloseUpdateDialog}
        aria-labelledby="update-dialog-title"
        aria-describedby="update-dialog-description"
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 10px 40px rgba(15, 23, 42, 0.15)'
          }
        }}
      >
        <DialogTitle id="update-dialog-title" sx={{ 
          backgroundColor: 'linear-gradient(135deg, #0f172a, #1e3a5f)',
          color: '#ffffff',
          fontWeight: 800,
          py: 2.5,
          px: 3
        }}>
          PERBARUI PROYEK?
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <DialogContentText id="update-dialog-description" sx={{ color: '#475569', fontSize: '1rem' }}>
            YAKIN INGIN MENYIMPAN PERUBAHAN PROYEK?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(15, 23, 42, 0.1)', gap: 1 }}>
          <Button 
            onClick={handleCloseUpdateDialog}
            sx={{ 
              color: '#64748B',
              fontWeight: 600,
              '&:hover': { 
                backgroundColor: 'rgba(15, 23, 42, 0.04)'
              }
            }}
          >
            BATAL
          </Button>
          <Button 
            onClick={handleUpdateProject} 
            autoFocus
            variant="contained"
            disabled={loading}
            sx={{ 
              bgcolor: 'linear-gradient(135deg, #3B82F6, #1e40af)',
              '&:hover': { bgcolor: 'linear-gradient(135deg, #2563EB, #1e3a8a)' },
              color: '#ffffff',
              fontWeight: 700,
              boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
              px: 3
            }}
          >
            {loading ? <CircularProgress size={20} sx={{ color: '#ffffff' }} /> : 'YA, PERBARUI PROYEK'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EditProject;
